"use client";

import React from "react";
import { ArrowRight, Shield, Building2, SearchCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const applicationCards = [{
  id: "narcotics",
  title: "Field Narcotics Screening",
  badge: "Field Operations",
  icon: SearchCheck,
  description: "Rapid, non-destructive identification of suspected illicit substances, novel psychoactive substances (NPS), and cutting agents directly through transparent packaging without sampling risk.",
  capabilities: "Point-of-contact verification • Minimal exposure risk • Immediate automated library matching",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/field_narcotics_screening.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/field_narcotics_screening.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/field_narcotics_screening.png-wvc-sizes",
  alt: "Handheld Raman spectroscopy device screening unknown material in protective container",
  link: "/RamanHandheldAnalyzersPage",
  actionText: "View Screening Systems"
}, {
  id: "law-enforcement",
  title: "Law Enforcement Facilities",
  badge: "Custody & Evidence",
  icon: Building2,
  description: "Chain-of-custody evidentiary material analysis within police stations, forensic intake units, and correctional centres, providing traceable reporting for legal proceedings.",
  capabilities: "Audit-ready reporting • Controlled intake verification • High-throughput evidence processing",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/law_enforcement_facility_analysis.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/law_enforcement_facility_analysis.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/law_enforcement_facility_analysis.png-wvc-sizes",
  alt: "Laboratory benchtop analytical instrument in a forensic evidence examination facility",
  link: "/LawEnforcementPublicSafetyPage",
  actionText: "Explore Station Solutions"
}, {
  id: "border-security",
  title: "Border Security & Checkpoints",
  badge: "Customs & Ports",
  icon: Shield,
  description: "High-throughput screening protocols for port-of-entry cargo, postal distribution hubs, and international transit corridors to intercept contraband and hazardous materials.",
  capabilities: "Through-barrier inspection • Ruggedized field deployment • Multi-agency data synchronization",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/border_security_inspection.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/border_security_inspection.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/border_security_inspection.png-wvc-sizes",
  alt: "Border security officer inspecting consignment parcels with handheld optical scanner",
  link: "/BorderSecurityAndDefencePage",
  actionText: "View Checkpoint Protocols"
}];
export default function ApplicationAreas() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="applicationareasgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-medium text-xs tracking-wider uppercase">
            Deployment Environments
          </Badge>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground font-default">
            Application-Led Analytical Capabilities
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Targeted spectroscopic instrumentation and standardized verification workflows configured for frontline security, forensic intake, and customs inspection.
          </p>
        </div>

        {/* 3-Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {applicationCards.map((card, index) => {
          const IconComponent = card.icon;
          return <motion.div key={card.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} whileInView={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.12
          }} className="flex flex-col h-full">
                <Card className="flex flex-col h-full overflow-hidden border border-border shadow-xs hover:shadow-md transition-shadow duration-200 bg-card">
                  {/* Card Visual Header */}
                  <div className="relative w-full aspect-16/10 overflow-hidden bg-muted">
                    <img src={card.image} data-wvc-srcset={card.srcset} data-wvc-sizes={card.sizes} alt={card.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
                    <div className="absolute top-3 left-3">
                      <Badge variant="secondary" className="backdrop-blur-md bg-secondary/90 border border-border text-xs font-semibold">
                        {card.badge}
                      </Badge>
                    </div>
                  </div>

                  {/* Card Main Info */}
                  <CardHeader className="pt-6 pb-3">
                    <div className="flex items-center gap-2.5 mb-2 text-primary">
                      <IconComponent className="size-5 shrink-0" />
                      <span className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
                        Operational Sector
                      </span>
                    </div>
                    <CardTitle className="text-xl font-semibold text-card-foreground font-default">
                      {card.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {card.description}
                    </CardDescription>
                  </CardHeader>

                  {/* Operational Capabilities List */}
                  <CardContent className="py-2 grow">
                    <div className="pt-3 border-t border-border flex items-start gap-2 text-xs text-muted-foreground">
                      <CheckCircle2 className="size-4 shrink-0 text-primary mt-0.5" />
                      <p className="leading-normal">{card.capabilities}</p>
                    </div>
                  </CardContent>

                  {/* Card Footer CTA */}
                  <CardFooter className="pt-4 pb-6">
                    <Button asChild variant="outline" className="w-full justify-between group">
                      <Link to={card.link}>
                        <span>{card.actionText}</span>
                        <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1 text-primary" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}